import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

/**
 * Die Eingabefelder der Anmeldestrecke — und nur ihrer.
 *
 * **Warum ein zweites Feld neben `ui/elements.tsx` `Field` steht.** Das Feld
 * der Anwendung ist 34 px hoch und trägt seine Kante selbst
 * (`ui/shadcn/input.tsx`); das Feld der Anmeldemaske ist 44 px hoch, liegt auf
 * `--secondary` und trägt links ein Sinnbild INNERHALB der Kante
 * (`claude-design-concept/login_export/SPEC.md`, «Maße»). Beides in einen
 * Baustein zu zwingen hiesse, `Field` ein halbes Dutzend Schalter zu geben,
 * die auf dreissig Seiten nie gesetzt werden. Die Anmeldestrecke ist der eine
 * Bildschirm dieser Anwendung mit eigener Bühne — vier Felder auf einer Seite,
 * die sonst nichts zeigt —, und sie bekommt deshalb ihre eigenen vier Zeilen.
 *
 * **Die Verbindung läuft über `for`/`id`, wie bei `Field` und aus demselben
 * Grund.** Der zugängliche Name eines Feldes ist der Text seiner Beschriftung,
 * und genau darüber finden ihn `e2e/harness.ts` («Passwort», exakt) und
 * `e2e/first-login.spec.ts` («Neues Passwort», exakt). Ein Hinweis oder ein
 * Sinnbild darf deshalb NICHT in die Beschriftung geraten: Aus «Neues
 * Passwort» würde «Neues Passwort Mindestens zwölf Zeichen», und beide
 * Prüfungen fänden das Feld nicht mehr.
 *
 * **Das Sinnbild ist `aria-hidden`.** Es wiederholt die Beschriftung als Bild
 * und sagt nichts, was nicht danebensteht.
 *
 * **Die Kante ist `border-strong` und nicht `border`, und das ist keine
 * Kosmetik, sondern der einzige verfügbare Kompromiss.** Die Fläche des
 * Feldes (`--secondary`) unterscheidet sich im Hellen von der Karte
 * (`--card`, weiss) fast nicht; die Ausdehnung des Feldes trägt dort die
 * Linie allein. `--border` misst dabei 1,25:1, `--fa-border-strong` 1,49:1 —
 * die 3:1 aus WCAG 1.4.11 erreicht **keiner** von beiden. Der Rückstand
 * `W-borderkontrast` bleibt davon unberührt offen; die Rechnung steht in
 * `src/styles.css` und in `ui/shadcn/input.tsx`, wo dieselbe Entscheidung aus
 * demselben Grund schon getroffen ist.
 */

/**
 * Die Klasse des `<input>` INNERHALB von `AnmeldeFeld`.
 *
 * Kante, Fläche und Innenabstand trägt der Rahmen — das Feld selbst ist nackt.
 * Ein Feld mit eigener Kante ergäbe eine zweite Linie innerhalb der ersten.
 *
 * **`text-base md:text-sm`, wie `inputVariants`.** Unter 16 px zoomt Safari auf
 * dem Telefon beim Hineintippen in das Feld hinein und kommt nicht wieder
 * heraus; ab der mittleren Breite ist das kein Thema mehr.
 */
export const ANMELDE_INPUT_CLASS =
  'h-full w-full min-w-0 border-0 bg-transparent p-0 text-base md:text-sm text-foreground ' +
  'outline-none placeholder:text-text3 ' +
  'selection:bg-primary selection:text-primary-foreground';

/**
 * Ein beschriftetes Feld der Anmeldestrecke: Beschriftung, dann ein 44 px
 * hoher Rahmen mit Sinnbild links, Feld in der Mitte und optional etwas
 * rechts.
 *
 * Der Fokusring sitzt am RAHMEN und nicht am Feld (`focus-within`) — sonst
 * läge er innerhalb der Kante und wäre auf 44 px Höhe kaum zu sehen.
 */
export function AnmeldeFeld(props: {
  readonly label: string;
  /** Das Sinnbild links, 18 px. Wird `aria-hidden` gesetzt. */
  readonly sinnbild: ReactNode;
  /** Ein Hinweis unter dem Feld. Steht AUSSERHALB der Beschriftung. */
  readonly hinweis?: string;
  /** Was rechts im Rahmen steht — der Sichtbarkeits-Umschalter. */
  readonly hinten?: ReactNode;
  /**
   * Eine EIGENE Kennung für das Feld statt der erzeugten.
   *
   * Genau ein Fall braucht das, und er ist keine Kosmetik
   * (`A-sitzungssperre`): Ein Passwortverwalter erkennt ein
   * Einmalkennwortfeld an `autocomplete="one-time-code"` **und** an
   * sprechenden `name`/`id`-Werten. `useId()` liefert `:r3:` — eine Kennung,
   * die bei jedem Rendern anders aussehen kann und aus der niemand etwas
   * ableiten kann.
   */
  readonly feldId?: string;
  /** Das `<input>`. Bekommt die erzeugte Kennung angehängt. */
  readonly children: ReactNode;
}): ReactNode {
  const erzeugt = useId();
  const id = props.feldId ?? erzeugt;
  const feld = isValidElement(props.children)
    ? cloneElement(props.children as ReactElement<{ id?: string }>, { id })
    : props.children;

  return (
    <div>
      <label htmlFor={id} className="mb-[7px] block text-xs font-medium text-muted-foreground">
        {props.label}
      </label>
      <div
        className={
          'flex h-11 items-center gap-2.5 rounded-[10px] border border-strong bg-secondary ' +
          'px-[13px] focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/40'
        }
      >
        <span aria-hidden="true" className="flex shrink-0 text-text3 [&>svg]:size-[18px]">
          {props.sinnbild}
        </span>
        {feld}
        {props.hinten === undefined ? null : props.hinten}
      </div>
      {props.hinweis === undefined ? null : (
        <p className="mt-[7px] text-xs text-muted-foreground">{props.hinweis}</p>
      )}
    </div>
  );
}
